import * as React from "react";
import ClockLoader from "react-spinners/ClockLoader";
import Grid from "@mui/material/Grid";
import Item from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import "bootstrap/dist/css/bootstrap.min.css";
import "./form.css";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Button } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomDatePicker from "../../helper/Form/DatePicker";
import IntPhoneNumberField from "../../helper/Form/IntPhoneNumber";
import AutocompleteSelect from "../../helper/Form/AutoSelect";
import Alerts from "../../helper/Alert";
import axios from "axios";
import dayjs from "dayjs";

const FILE_SIZE = 5 * 1024 * 1024; // 5MB

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required")
    .matches(
      /^[A-Za-z][A-Za-z.\s]*$/,
      "Must start with a letter and only contain letters, dots, and spaces"
    )
    .max(30, " cannot exceed 30 characters")
    .test(
      "not-all-same-characters",
      "Name cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
  lastName: yup
    .string()
    .required("Last Name is required")
    .matches(
      /^[A-Za-z][A-Za-z.\s]*$/,
      "Must start with a letter and only contain letters, dots, and spaces"
    )
    .max(30, " cannot exceed 30 characters")
    .test(
      "not-all-same-characters",
      "Name cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
  // dob: yup
  //   .mixed() // Use `mixed` instead of `string` if storing Day.js object
  //   .required('Date of Birth is required')
  //   .test('is-valid-date', 'Invalid date', (value) => {
  //     if (!value) return false;
  //     // If value is a string (DD/MM/YYYY)
  //     if (typeof value === 'string') {
  //       return dayjs(value, 'DD/MM/YYYY', true).isValid();
  //     }
  //     // If value is a Day.js object
  //     return value.isValid();
  //   })
  //   .test('is-past-date', 'Date must be in the past', (value) => {
  //     if (!value) return false;
  //     const date = typeof value === 'string' ? dayjs(value, 'DD/MM/YYYY') : value;
  //     return date.isBefore(dayjs());
  //   }),
  // dob: yup
  //   .string()
  //   .required('Date of Birth is required')
  //   .test('is-valid-date', 'Invalid date format or value', (value) => {
  //     if (!value) return false;

  //     const cleanValue = value.trim();
  //     const parsed = dayjs(cleanValue, 'DD/MM/YYYY', true);

  //     if (!parsed.isValid()) return false;

  //     const [day, month, year] = cleanValue.split('/').map(Number);

  //     if (day < 1 || day > 31) return false;
  //     if (month < 1 || month > 12) return false;
  //     if (year < 1900 || year > new Date().getFullYear()) return false;

  //     return true;
  //   })
  //   .test('is-past-date', 'Date must be in the past', (value) => {
  //     if (!value) return false;
  //     const parsed = dayjs(value.trim(), 'DD/MM/YYYY', true);
  //     return parsed.isValid() && parsed.isBefore(dayjs());
  //   }),
  dob: yup
    .date()
    .required("Date of Birth is required")
    .min(dayjs("1900-01-01"), "Date must be after 01/01/1990")
    .max(dayjs(), "Date cannot be in the future")
    .typeError("Please enter a valid date"),
  email: yup
    .string()
    .required("Email is required")
    .test(
      "no-spaces",
      "Email must not contain spaces",
      (value) => !/\s/.test(value || "")
    )
    .test(
      "valid-local-part",
      "Invalid email format: special characters cannot start/end or repeat in local part",
      (value) => {
        if (!value) return false;
        const [localPart] = value.split("@");
        // Disallow starting/ending with . _ -
        if (/^[._-]|[._-]$/.test(localPart)) return false;
        // Disallow consecutive special chars
        if (/([._-])\1+/.test(localPart)) return false;
        return true;
      }
    )
    .test(
      "must-contain-letter-in-local-part",
      "Email must contain at least one letter",
      (value) => {
        if (!value) return false;
        const [localPart] = value.split("@");
        return /[A-Za-z]/.test(localPart); // Must contain at least one letter
      }
    )
    .email("Invalid email"),
  phonenumber: yup
    .string()
    .required("Phone number is required")
    .matches(
      /^\+91 [6-9]\d{4} \d{5}$/, // Ensures format +91 XXXXX XXXXX
      "Phone number must be in the format +91 XXXXX XXXXX and start with 6 to 9"
    )
    .test(
      "is-ten-digits",
      "Phone number must contain exactly 10 digits",
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, ""); // Remove non-numeric characters
        return digits.length === 12 && digits.startsWith("91"); // 10 digits + 91
      }
    ),
  address: yup
    .string()
    .required("Address is required")
    .matches(
      /^[A-Za-z0-9]/,
      "Address must start with a letter or number (no special characters or spaces)"
    )
    .test(
      "has-letter",
      "Address must contain at least one alphabetic character",
      (value) => /[A-Za-z]/.test(value || "")
    )
    .test(
      "not-numeric-only",
      "Address cannot be only numbers",
      (value) => !/^\d+$/.test(value || "")
    ),
  // .max(100, "Address cannot exceed 100 characters"),
  city: yup
    .string()
    .required("City is required")
    // .matches(/^[A-Za-z]+$/, 'City must contain only alphabetic characters')
    .matches(
      /^[A-Za-z]+(?: [A-Za-z]+)*$/,
      "Must start with a letter, only contain letters and single spaces between words"
    )
    .max(30, "City cannot exceed 30 characters")
    .test(
      "not-all-same-characters",
      "City cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
  state: yup.string().required("State is required"),
  district: yup.string().required("District is required"),
  zip: yup
    .string()
    .required("Pin code is required")
    .matches(/^\d{6}$/, "Pin code must be exactly 6 digits")
    .min(6, "Pin code must be exactly 6 digits")
    .max(6, "Pin code must be exactly 6 digits")
    .notOneOf(["000000"], "Pin code cannot be all zeros"),

  aadharNumber: yup
    .string()
    .required("Aadhar number is required")
    .matches(
      /^[2-9][0-9]{11}$/,
      "Aadhar number must be 12 digits and cannot start with 0 or 1"
    )
    .notOneOf(["000000000000"], "Pin code cannot be all zeros"),

  aadharCardFront: yup
    .mixed()
    .nullable()
    .test(
      "fileRequired",
      "Aadhar card front side image is required",
      function (value) {
        return (
          (typeof value === "string" && value !== "") ||
          (value && value.length > 0)
        );
      }
    )
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),

  aadharCardBack: yup
    .mixed()
    .nullable()
    .test(
      "fileRequired",
      "Aadhar card back side image is required",
      function (value) {
        return (
          (typeof value === "string" && value !== "") ||
          (value && value.length > 0)
        );
      }
    )
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),

  passportSizePhotoFile: yup
    .mixed()
    .nullable()
    .test("fileRequired", "Passport size photo is required", function (value) {
      return (
        (typeof value === "string" && value !== "") ||
        (value && value.length > 0)
      );
    })
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),

  marksheetsFile: yup
    .mixed()
    .nullable()
    .test("fileRequired", "Marksheet is required", function (value) {
      return (
        (typeof value === "string" && value !== "") ||
        (value && value.length > 0)
      );
    })
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),
  proofOfIncomeFile: yup
    .mixed()
    .nullable()
    .test("fileRequired", "Income certificate is required", function (value) {
      return (
        (typeof value === "string" && value !== "") ||
        (value && value.length > 0)
      );
    })
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),
  usaReferenceLetter: yup
    .mixed()
    .nullable()
    .test("fileRequired", "Reference letter is required", function (value) {
      return (
        (typeof value === "string" && value !== "") ||
        (value && value.length > 0)
      );
    })
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),

  bonafideCertificate: yup
    .mixed()
    .nullable()
    .test("fileRequired", "bonafide Certificate is required", function (value) {
      console.log("VALUE bonaf", value);
      return (
        (typeof value === "string" && value !== "") ||
        (value && value.length > 0)
      );
    })
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),
  msetScholarshipLetter: yup
    .mixed()
    .nullable()
    .test(
      "fileRequired",
      "MSET scholarship form is required",
      function (value) {
        console.log("VALUE msed", value);
        return (
          (typeof value === "string" && value !== "") ||
          (value && value.length > 0)
        );
      }
    )
    .test("fileSize", "File size should be less than 2MB", (value) => {
      if (typeof value === "string") return true;
      const file = value && value[0];
      return file && file.size <= FILE_SIZE;
    }),
});
export default function Step1({
  formData,
  setFormData,
  handleNext,
  handleBack,
  setUpdateApplicantDetails,
  updateApplicantDetails
}) {
  const {
    handleSubmit,
    control,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formData,
  });

  const [applicantId, setApplicantId] = React.useState("");

  React.useEffect(() => {
    const myData = localStorage.getItem("volunteersDetail");
    if (myData) {
      const storedDataString = localStorage.getItem("volunteersDetail");
      const storedData = JSON.parse(storedDataString);
      setApplicantId(storedData.volunteersDetail.id);
    }
  }, []);

  const [alert, setAlert] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  const [files, setFiles] = React.useState([
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ]);

  const [fileUpdateFlags, setFileUpdateFlags] = React.useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  const [loading, setLoading] = React.useState(false);

  const fileSelected = (index, event) => {
    const file = event.target.files[0];
    const updatedFiles = [...files];
    updatedFiles[index] = file;
    setFiles(updatedFiles);

    const updatedFlags = [...fileUpdateFlags];
    updatedFlags[index] = true;
    setFileUpdateFlags(updatedFlags);
  };
  const API_URL = process.env.React_App_Api;

  const handleCloseAlert = () => {
    setAlert({
      open: false,
      message: "",
      severity: "",
    });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (updateApplicantDetails) {
        const UpdatePayload = {
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: data.dob,
          aadharNumber: data.aadharNumber,
          appemail: data.email,
          phoneNumber: data.phonenumber,
          addressone: data.address,
          city: data.city,
          state: data.state,
          district: data.district,
          pincode: data.zip,
        };
        const currentFolderName = `${formData.firstName}${formData.lastName}${formData.aadharNumber.slice(-4)}`;
        const fileFields = [
          'aadharCardFront',
          'aadharCardBack',
          'passportSizePhotoFile',
          'marksheetsFile',
          'proofOfIncomeFile',
          'usaReferenceLetter',
          'bonafideCertificate',
          'msetScholarshipLetter'
        ];
        for (let i = 0; i < files.length; i++) {
          if (fileUpdateFlags[i] && files[i]) {
            const fileData = new FormData();
            fileData.append("file", files[i]);
            fileData.append("folderName", currentFolderName);
            fileData.append("fileField", fileFields[i]);
            await axios
              .post(`${API_URL}/file/updatefile/${applicantId}`, fileData)
              .catch((error) => {
                console.log('ERROR', error);
                let errorMessage = 'Sorry! Something went wrong please try again later';
                if (error.response && error.response.status === 404) {
                  errorMessage = error.response.statusText;
                  return setAlert({
                    open: true,
                    message: `Sorry! ${errorMessage} in S3`,
                    severity: 'error',
                  });
                }
                setAlert({
                  open: true,
                  message: errorMessage,
                  severity: 'error',
                });
                setLoading(false);
              });
          }
        }
        const prevDataString = localStorage.getItem("myUsaDatastep1");
        const prevData = prevDataString ? JSON.parse(prevDataString)?.step1 : null;

        // Construct only the changed fields
        let updatedFields = {};
        if (applicantId && prevData) {
          Object.entries(UpdatePayload).forEach(([key, value]) => {
            if (prevData[key] !== value) {
              updatedFields[key] = value;
            }
          });
        }
        if (Object.keys(updatedFields).length > 0) {
          await axios.patch(`${API_URL}/aid/applicantDetailsUpdateWithoutFiles/${applicantId}`, updatedFields);
          const prevDataString = localStorage.getItem("myUsaDatastep1");
          const prevData = prevDataString ? JSON.parse(prevDataString)?.step1 : {};
          const updatedLocalStorage = {
            ...prevData,
            ...updatedFields,
          };

          localStorage.setItem("myUsaDatastep1", JSON.stringify({ step1: updatedLocalStorage }));
          setAlert({
            open: true,
            message: 'Applicant details updated successfully',
            severity: "success",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
          handleNext();
          setUpdateApplicantDetails(false);
        } else {
          setAlert({
            open: true,
            message: 'No Data changed',
            severity: "success",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
          setTimeout(() => {
            handleNext();
          }, 2000);
        }
      } else {
        let dataobj = [];
        const fileData = new FormData();
        for (let i = 0; i < files.length; i++) {
          fileData.append("images", files[i]);
        }
        const folderName = `${data.firstName}${data.lastName
          }${data.aadharNumber.slice(-4)}`;
        fileData.append("folderName", folderName);
        console.log("FILES", files);
        console.log("FILES", files.length);
        // if (files.length > 0) {
          if (files.some(file => file)) {
          await axios.post(`${API_URL}/file/posts`, fileData).then((res) => {
            dataobj = res.data.files;
          });
        }
        const payload = {
          applicant: {
            firstName: data.firstName,
            lastName: data.lastName,
            dob: data.dob,
            aadharcardnumber: data.aadharNumber,
            appemail: data.email,
            phonenumber: data.phonenumber,
            aadharCardFront: dataobj[0] ?? null,
            aadharCardBack: dataobj[1] ?? null,
            passportSizePhotoFile: dataobj[2] ?? null,
            marksheetsFile: dataobj[3] ?? null,
          },
          address: {
            addressone: data.address,
            city: data.city,
            state: data.state,
            district: data.district,
            pincode: data.zip,
          },
          parentorguardian: {
            proofOfIncomeFile: dataobj[4] ?? null,
          },
          usavolunteer: {
            usaReferenceLetter: dataobj[5] ?? null
          },
          institutiondetails: {
            bonafideCertificate: dataobj[6] ?? null,
          },
          msetScholarshipLetter: dataobj[7] ?? null,
        };
        await axios
          .patch(`${API_URL}/aid/applicantdetailsupdate/` + applicantId, payload)
          .then((res) => {
            handleNext();
            const myData = {
              step1: {
                id: res.data._id,
                firstName: res.data.applicant.firstName,
                lastName: res.data.applicant.lastName,
                phoneNumber: res.data.applicant.phonenumber,
                aadharNumber: data.aadharNumber,
                dateOfBirth: res.data.applicant.dob,
                appEmail: res.data.applicant.appemail,
                addressone: res.data.address.addressone,
                city: res.data.address.city,
                state: res.data.address.state,
                district: res.data.address.district,
                pincode: res.data.address.pincode,
              },
            };
            const storeStep1 = JSON.stringify(myData);
            localStorage.setItem("myUsaDatastep1", storeStep1);
          });
      }
    } catch (error) {
      if (error.response?.data?.statusCode === 409) {
        return setAlert({
          open: true,
          message: error.response.data?.error,
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } else if (error.response?.status === 503) {
        return setAlert({
          open: true,
          message: "Database is down. Please try again later.",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } else if (error.response) {
        return setAlert({
          open: true,
          message:
            error.response.data ||
            "An unexpected error occurred While uploading file. Please try again later.",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } else if (error.message) {
        return setAlert({
          open: true,
          message: error.message || "Server Error.Please try again later",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } else {
        // Handle other errors
        setAlert({
          open: true,
          message: "An unexpected error occurred. Please try again later.",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });

        // Reload the project
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      setLoading(false); // Set loading state to false when form submission completes (whether success or error)
    }
    setFormData((prevData) => ({
      ...prevData,
      ...data,
    }));
  };

  const tamilNaduDistricts = [
    "Ariyalur",
    "Chengalpattu",
    "Chennai",
    "Coimbatore",
    "Cuddalore",
    "Dharmapuri",
    "Dindigul",
    "Erode",
    "Kallakurichi",
    "Kanchipuram",
    "Kanyakumari",
    "Karur",
    "Krishnagiri",
    "Madurai",
    "Mayiladuthurai",
    "Nagapattinam",
    "Namakkal",
    "Nilgiris",
    "Perambalur",
    "Pudukkottai",
    "Ramanathapuram",
    "Ranipet",
    "Salem",
    "Sivaganga",
    "Tenkasi",
    "Thanjavur",
    "Theni",
    "Thoothukudi",
    "Tiruchirappalli",
    "Tirunelveli",
    "Tirupathur",
    "Tiruppur",
    "Tiruvallur",
    "Tiruvannamalai",
    "Tiruvarur",
    "Vellore",
    "Viluppuram",
    "Virudhunagar",
  ];

  return (
    <>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <ClockLoader
            color="#1976d2"
            loading={loading}
            size={150}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      ) : (
        <Box>
          <Grid
            container
            spacing={2}
            p={2}
            sx={{
              display: "flex",
            }}
          >
            <Grid item md={2} sm={12}>
              <Controller
                name="firstName"
                control={control}
                defaultValue={formData.firstName || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        First Name <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter First Name"
                    size="small"
                    className="responsive-form"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    sx={{ width: 270, maxWidth: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2} sm={12}>
              <Controller
                name="lastName"
                control={control}
                defaultValue={formData.lastName || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        Last Name <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter last Name"
                    size="small"
                    className="responsive-form"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    sx={{ width: 270, maxWidth: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2.5} sm={12} sx={{ marginTop: "-7px" }}>
              <CustomDatePicker
                label="Date of Birth *"
                name="dob"
                control={control}
                defaultValue={formData.dob}
                disableFuture={true}
                error={errors.dob}
                className="date"
              />
            </Grid>
            <Grid item md={3} sm={12}>
              <Controller
                name="email"
                control={control}
                defaultValue={formData.email || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        Email <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter Email"
                    size="small"
                    className="responsive-form"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{
                      width: 300,
                      maxWidth: "100%",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2.5} sm={12}>
              <IntPhoneNumberField
                control={control}
                defaultValue={formData.phonenumber || ""}
                name="phonenumber"
                label={
                  <>
                    Phone Number <span style={{ color: "orangered" }}>*</span>
                  </>
                }
                // required={false}
                error={errors.phonenumber}
              />
            </Grid>
            {/* 2 Row */}
            <Grid item md={4} sm={12}>
              <Controller
                name="address"
                control={control}
                defaultValue={formData.address || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        Address with Door No{" "}
                        <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter address"
                    size="small"
                    className="responsive-form"
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    sx={{
                      width: 400,
                      maxWidth: "100%",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2} sm={12}>
              <Controller
                name="city"
                control={control}
                defaultValue={formData.city || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        City <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter city"
                    size="small"
                    className="responsive-form"
                    error={!!errors.city}
                    helperText={errors.city?.message}
                    sx={{
                      width: 400,
                      maxWidth: "100%",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={2} sm={12}>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{
                      width: 270,
                      maxWidth: "100%",
                    }}
                    size="small"
                    error={!!errors.state}
                  >
                    <InputLabel id="demo-select-small">
                      State <span style={{ color: "orangered" }}>*</span>
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="demo-select-small"
                      id="demo-select-small"
                      label="State *"
                      className="responsive-form"
                      defaultValue="usa"
                      disabled={true}
                    >
                      <MenuItem value="Tamil Nadu">Tamil Nadu</MenuItem>
                    </Select>
                    <FormHelperText>{errors.state?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item md={2} sm={12}>
              <AutocompleteSelect
                control={control}
                defaultValue={formData.district || ""}
                name="district"
                label={
                  <>
                    District <span style={{ color: "orangered" }}>*</span>
                  </>
                }
                size="small"
                options={tamilNaduDistricts}
                error={errors.district}
              />
            </Grid>
            <Grid item md={2} sm={12}>
              <Controller
                name="zip"
                control={control}
                defaultValue={formData.zip || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        PIN Code <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter PIN Code"
                    size="small"
                    className="responsive-form"
                    error={!!errors.zip}
                    helperText={errors.zip?.message}
                    inputProps={{ maxLength: 6 }}
                    sx={{
                      width: 300,
                      maxWidth: "100%",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item md={3} sm={12}>
              <Controller
                name="aadharNumber"
                control={control}
                defaultValue={formData.aadharNumber || ""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      <label>
                        Aadhar Number{" "}
                        <span style={{ color: "orangered" }}>*</span>
                      </label>
                    }
                    placeholder="Enter aadharNumber"
                    size="small"
                    className="responsive-form"
                    error={!!errors.aadharNumber}
                    helperText={errors.aadharNumber?.message}
                    inputProps={{ maxLength: 12 }}
                    sx={{
                      width: 300,
                      maxWidth: "100%",
                    }}
                  />
                )}
              />
            </Grid>
            {/* Attachments */}
            <Grid item md={12} sm={12} sx={{ marginBottom: "-15px" }}>
              <Item>
                <Typography
                  gutterBottom
                  component="div"
                  className="fw-semibold text-primary"
                >
                  Attachments
                </Typography>
              </Item>
            </Grid>

            <Grid item md={6} sm={12}>
              <Typography
                sx={{ mb: 0.5 }}
                className="fs-6 text-dark-emphasis fw-normal"
              >
                Upload Aadhar Card Front Side Image{" "}
                <span style={{ color: "orangered" }}>*</span>
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                File size should be less than 2MB
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                Only JPG, PNG, and PDF files are allowed
              </Typography>
              <div className="input-group">
                <label
                  htmlFor="inputGroupFileAadharCardFront"
                  className="form-control border-primary"
                >
                  {/* {files[0] ? files[0].name : "Choose a file"} */}
                  {files[0]?.name || formData.aadharCardFront?.split("/").pop() || "Choose a file"}
                </label>
                <Controller
                  name="aadharCardFront"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      className="visually-hidden"
                      id="inputGroupFileAadharCardFront"
                      aria-describedby="inputGroupFileAddonAadharCardFront"
                      aria-label="Upload"
                      onChange={(e) => {
                        field.onChange(e.target.files);
                        fileSelected(0, e);
                      }}
                      required
                    />
                  )}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  id="inputGroupFileAddonAadharCardFront"
                  onClick={() => {
                    document
                      .getElementById("inputGroupFileAadharCardFront")
                      .click();
                  }}
                >
                  <CloudUploadIcon />
                </button>
              </div>

              {errors.aadharCardFront && (
                <Typography variant="caption" color="error">
                  {errors.aadharCardFront.message}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} sm={12}>
              <Typography
                sx={{ mb: 0.5 }}
                className="fs-6 text-dark-emphasis fw-normal"
              >
                Upload Aadhar Card Back Side Image{" "}
                <span style={{ color: "orangered" }}>*</span>
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                File size should be less than 2MB
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                Only JPG, PNG, and PDF files are allowed
              </Typography>
              <div className="input-group">
                <label
                  htmlFor="inputGroupFileAadharCardBack"
                  className="form-control border-primary"
                >
                  {files[1]?.name || formData.aadharCardBack?.split("/").pop() || "Choose a file"}
                </label>
                <Controller
                  name="aadharCardBack"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      className="visually-hidden"
                      id="inputGroupFileAadharCardBack"
                      aria-describedby="inputGroupFileAddonAadharCardBack"
                      aria-label="Upload"
                      onChange={(e) => {
                        field.onChange(e.target.files);
                        fileSelected(1, e);
                      }}
                      required
                    />
                  )}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  id="inputGroupFileAddonAadharCardBack"
                  onClick={() => {
                    document
                      .getElementById("inputGroupFileAadharCardBack")
                      .click();
                  }}
                >
                  <CloudUploadIcon />
                </button>
              </div>

              {errors.aadharCardBack && (
                <Typography variant="caption" color="error">
                  {errors.aadharCardBack.message}
                </Typography>
              )}
            </Grid>

            {/* Passport Size Photo */}
            <Grid item md={6} sm={12}>
              <Typography
                sx={{ mb: 0.5 }}
                className="fs-6 text-dark-emphasis fw-normal"
              >
                Upload Passport Size Photo{" "}
                <span style={{ color: "orangered" }}>*</span>
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                File size should be less than 2MB
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                Only JPG, PNG, and PDF files are allowed
              </Typography>
              <div className="input-group">
                <label
                  htmlFor="inputGroupFilePassportSizePhoto"
                  className="form-control border-primary"
                >
                  {files[2]?.name || formData.passportSizePhotoFile?.split("/").pop() || "Choose a file"}
                </label>
                <Controller
                  name="passportSizePhotoFile"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      className="visually-hidden"
                      id="inputGroupFilePassportSizePhoto"
                      aria-describedby="inputGroupFileAddonPassportSizePhoto"
                      aria-label="Upload"
                      onChange={(e) => {
                        field.onChange(e.target.files);
                        fileSelected(2, e);
                      }}
                      required
                    />
                  )}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  id="inputGroupFileAddonPassportSizePhoto"
                  onClick={() => {
                    document
                      .getElementById("inputGroupFilePassportSizePhoto")
                      .click();
                  }}
                >
                  <CloudUploadIcon />
                </button>
              </div>

              {errors.passportSizePhotoFile && (
                <Typography variant="caption" color="error">
                  {errors.passportSizePhotoFile.message}
                </Typography>
              )}
            </Grid>

            {/* Marksheet */}
            <Grid item md={6} sm={12}>
              <Typography
                sx={{ mb: 0.5 }}
                className="fs-6 text-dark-emphasis fw-normal"
              >
                Upload Marksheet <span style={{ color: "orangered" }}>*</span>
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                File size should be less than 2MB
              </Typography>
              <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                Only JPG, PNG, and PDF files are allowed
              </Typography>
              <div className="input-group">
                <label
                  htmlFor="inputGroupFileMarkSheet"
                  className="form-control border-primary"
                >
                  {files[3]?.name || formData.marksheetsFile?.split("/").pop() || "Choose a file"}
                </label>
                <Controller
                  name="marksheetsFile"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      className="visually-hidden"
                      id="inputGroupFileMarkSheet"
                      aria-describedby="inputGroupFileAddonMarkSheet"
                      aria-label="Upload"
                      onChange={(e) => {
                        field.onChange(e.target.files);
                        fileSelected(3, e);
                      }}
                      required
                    />
                  )}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  id="inputGroupFileAddonMarkSheet"
                  onClick={() => {
                    document.getElementById("inputGroupFileMarkSheet").click();
                  }}
                >
                  <CloudUploadIcon />
                </button>
              </div>
              {errors.marksheetsFile && (
                <Typography variant="caption" color="error">
                  {errors.marksheetsFile.message}
                </Typography>
              )}
            </Grid>

            <Grid item md={6} sm={12}>
              <>
                <Typography
                  sx={{ mb: 0.5 }}
                  className="fs-6 text-dark-emphasis fw-normal"
                >
                  Proof of Income <span style={{ color: "orangered" }}>*</span>
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  File size should be less than 2MB
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  Only JPG, PNG, and PDF files are allowed
                </Typography>
                <div className="input-group">
                  <label
                    htmlFor="inputGroupFileProofOfIncome"
                    className="form-control border-primary"
                  >
                    {files[4]?.name || formData.proofOfIncomeFile?.split("/").pop() || "Choose a file"}
                  </label>

                  <Controller
                    name="proofOfIncomeFile"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="file"
                        className="form-control border-primary visually-hidden"
                        id="inputGroupFileProofOfIncome"
                        aria-describedby="inputGroupFileAddonProofOfIncome"
                        aria-label="Upload"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          fileSelected(4, e);
                        }}
                        required
                      />
                    )}
                  />
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    id="inputGroupFileAddonProofOfIncome"
                    onClick={() => {
                      document
                        .getElementById("inputGroupFileProofOfIncome")
                        .click();
                    }}
                  >
                    <CloudUploadIcon />
                  </button>
                </div>
              </>
              {errors.proofOfIncomeFile && (
                <Typography variant="caption" color="error">
                  {errors.proofOfIncomeFile.message}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} sm={12}>
              <>
                <Typography
                  sx={{ mb: 0.5 }}
                  className="fs-6 text-dark-emphasis fw-normal"
                >
                  Reference Letter <span style={{ color: "orangered" }}>*</span>
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  File size should be less than 2MB
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  Only JPG, PNG, and PDF files are allowed
                </Typography>
                <div className="input-group">
                  <label
                    htmlFor="inputGroupFileReferenceLetter"
                    className="form-control border-primary"
                  >
                    {files[5]?.name || formData.usaReferenceLetter?.split("/").pop() || "Choose a file"}
                  </label>
                  <Controller
                    name="usaReferenceLetter"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="file"
                        className="form-control border-primary visually-hidden"
                        id="inputGroupFileReferenceLetter"
                        aria-describedby="inputGroupFileAddonReferenceLetter"
                        aria-label="Upload"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          fileSelected(5, e);
                        }}
                        required
                      />
                    )}
                  />
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    id="inputGroupFileAddonReferenceLetter"
                    onClick={() => {
                      document
                        .getElementById("inputGroupFileReferenceLetter")
                        .click();
                    }}
                  >
                    <CloudUploadIcon />
                  </button>
                </div>

                {errors.usaReferenceLetter && (
                  <Typography variant="caption" color="error">
                    {errors.usaReferenceLetter.message}
                  </Typography>
                )}
              </>
            </Grid>

            <Grid item md={6} sm={12}>
              <>
                <Typography
                  sx={{ mb: 0.5 }}
                  className="fs-6 text-dark-emphasis fw-normal"
                >
                  Upload Fee Details in College Letter Head issued by the
                  College <span style={{ color: "orangered" }}>*</span>
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  File size should be less than 2MB
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  Only JPG, PNG, and PDF files are allowed
                </Typography>
                <div className="input-group">
                  <label
                    htmlFor="inputGroupFileBonafideCertificate"
                    className="form-control border-primary"
                  >
                    {files[6]?.name || formData.bonafideCertificate?.split("/").pop() || "Choose a file"}
                  </label>
                  <Controller
                    name="bonafideCertificate"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="file"
                        className="form-control border-primary visually-hidden"
                        id="inputGroupFileBonafideCertificate"
                        aria-describedby="inputGroupFileAddonBonafideCertificate"
                        aria-label="Upload"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          fileSelected(6, e);
                        }}
                        required
                      />
                    )}
                  />
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    id="inputGroupFileAddonBonafideCertificate"
                    onClick={() => {
                      document
                        .getElementById("inputGroupFileBonafideCertificate")
                        .click();
                    }}
                  >
                    <CloudUploadIcon />
                  </button>
                </div>
              </>
              {errors.bonafideCertificate && (
                <Typography variant="caption" color="error">
                  {errors.bonafideCertificate.message}
                </Typography>
              )}
            </Grid>

            <Grid item md={6} sm={12} width="400px">
              <>
                <Typography
                  sx={{ mb: 0.5 }}
                  className="fs-6 text-dark-emphasis fw-normal"
                >
                  MSET Scholarship Letter{" "}
                  <span style={{ color: "orangered" }}>*</span>
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  File size should be less than 2MB
                </Typography>
                <Typography sx={{ mb: 1, fontSize: "12px", color: "blue" }}>
                  Only JPG, PNG, and PDF files are allowed
                </Typography>
                <div className="input-group">
                  <label
                    htmlFor="inputGroupFileMSEDLetter"
                    className="form-control border-primary"
                  >
                    {files[7]?.name || formData.msetScholarshipLetter?.split("/").pop() || "Choose a file"}
                  </label>
                  <Controller
                    name="msetScholarshipLetter"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="file"
                        className="form-control border-primary visually-hidden"
                        id="inputGroupFileMSEDLetter"
                        aria-describedby="inputGroupFileAddonReferenceLetter"
                        aria-label="Upload"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          fileSelected(7, e);
                        }}
                        required
                      />
                    )}
                  />
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    id="inputGroupFileMSEDLetter"
                    onClick={() => {
                      document
                        .getElementById("inputGroupFileMSEDLetter")
                        .click();
                    }}
                  >
                    <CloudUploadIcon />
                  </button>
                </div>
                {/* Display file selection error */}
                {errors.msetScholarshipLetter && (
                  <Typography variant="caption" color="error">
                    {errors.msetScholarshipLetter.message}
                  </Typography>
                )}
              </>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex' }}>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
              Back
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ mt: 1, mr: 1 }}
            >
              Save & Continue
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}
